/**
 * Process Manager
 * 
 * Manages process lifecycle: creation, starting, stopping, and tracking.
 * This is the orchestrator that creates ProcessContext instances and starts processes.
 * 
 * The Process Manager is responsible for:
 * - Creating processes via the registry
 * - Creating ProcessContext instances
 * - Starting processes with their contexts
 * - Tracking running processes
 * - Handling process lifecycle (stop, cleanup)
 * - Integrating with Viewport Allocation Manager (future)
 */

import type { Process } from './process.js';
import type { ProcessContext } from './process.js';
import { createProcessContext, ProcessLifecycleMode } from './process.js';
import { createProcess } from './registry.js';
import { bus } from '../core/events.js';
import { log, trace } from '../services/logging/index.js';
import { processErrorBoundary } from './process-error-boundary.js';
import { state, updateState, setState } from '../core/state.js';
import { viewManager } from './view-manager.js';

/**
 * Process instance with its context
 */
interface ProcessInstance {
  process: Process;
  context: ProcessContext;
  startTime: number;
}

/**
 * Canonical process IDs for singleton/system processes.
 * Any attempt to start these kinds with arbitrary IDs will be coerced to the canonical ID.
 */
export const SINGLETON_PROCESS_IDS: Record<string, string> = {
  auth: 'auth-main',
  notifications: 'notifications-main',
  'cortex-details': 'cortex-details-main',
  microphone: 'microphone-main',
  visualization: 'visualization-main',
  drawing: 'drawing-main',
  settings: 'settings-main'
};

/**
 * Process Manager
 * 
 * Manages all processes in the system.
 */
export class ProcessManager {
  private processes: Map<string, ProcessInstance> = new Map();
  private activeProcessLogInterval: number | null = null;

  constructor() {
    this.registerEventListeners();
    
    // Listen for squares.allocated to capture grid info after allocation
    bus.on('squares.allocated', () => {
      this.captureProcessViewGridInfo();
    });

    this.startActiveProcessLogger();
  }
  
  /**
   * Capture grid allocation, position, and expanded state for all process views
   * Called after grid allocation to persist view grid state
   */
  private captureProcessViewGridInfo(): void {
    if (!state.processes) return;
    
    for (const [processId, processInfo] of Object.entries(state.processes)) {
      const context = this.getProcessContext(processId);
      if (!context) continue;
      
      const views = context.getViews?.() || [];
      const viewGridInfo = views.map(view => {
        const viewSquares = viewManager.getViewSquares(view.id) as any[];
        
        // Get grid allocation and position from first square (for single-square views)
        // For multi-square views, use the first square's position
        if (viewSquares.length > 0) {
          const square = viewSquares[0];
          return {
            id: view.id,
            gridAllocation: square.hidden || square.actualCols === 0 || square.actualRows === 0
              ? undefined
              : {
                  cols: square.actualCols,
                  rows: square.actualRows
                },
            gridPosition: square.hidden || square.x === -1 || square.y === -1
              ? undefined
              : {
                  x: square.x,
                  y: square.y
                },
            expanded: view.expanded ?? false
          };
        }
        
        // No square allocated yet
        return {
          id: view.id,
          expanded: view.expanded ?? false
        };
      });
      
      // Update process state with view grid info
      updateState('processes', (draft) => {
        if (draft && draft[processId]) {
          draft[processId].views = viewGridInfo;
        }
      });
    }
  }

  /**
   * Register event listeners for ProcessManager
   * Can be called again after clearing event bus (e.g., in tests)
   */
  private registerEventListeners(): void {
    // Listen for process.start events from EventBus
    // This allows processes to start other processes via events
    bus.on('process.start', (payload: unknown) => {
      const data = payload as { kind: string; id?: string; initialState?: Record<string, unknown> };
      if (data.kind) {
        const traceId = trace.start('process-manager-handle-start-event');

        const processId = data.id || `${data.kind}-${Date.now()}`;
        this.startProcess(data.kind, processId, data.initialState || {});

        trace.end(traceId);
      }
    });

    // Listen for dismiss events (uniform across all processes)
    bus.on('process.view.dismiss', (payload: unknown) => {
      const data = payload as { processId: string; viewId: string };
      if (data.processId && data.viewId) {
        const traceId = trace.start('process-manager-handle-dismiss');

        const context = this.getProcessContext(data.processId);
        if (context) {
          context.destroyView(data.viewId);
        } else {
        }

        trace.end(traceId);
      }
    });

    // Listen for process.stop events (from lifecycle management)
    bus.on('process.stop', (payload: unknown) => {
      const data = payload as { processId: string };
      if (data.processId) {
        const traceId = trace.start('process-manager-handle-stop-event');
        this.stopProcess(data.processId);
        trace.end(traceId);
      }
    });
  }

  /**
   * Periodically log the list of active processes from central state
   */
  private startActiveProcessLogger(): void {
    if (this.activeProcessLogInterval !== null) {
      return;
    }

    this.activeProcessLogInterval = window.setInterval(() => {
      const processesInState = state.processes || {};
      const entries = Object.entries(processesInState);
      const summary = entries.map(([id, info]) => `${id} (${(info as any)?.kind ?? 'unknown'})`);

      log.debug(
        `[ProcessManager] Active processes in state (${summary.length}): ${summary.length ? summary.join(', ') : 'none'}`
      );
    }, 2000);
  }

  /**
   * Re-register event listeners after clearing event bus (e.g., in tests)
   * Exported for test setup
   */
  _registerEventListeners(): void {
    this.registerEventListeners();
  }

  /**
   * Start a process by kind and ID
   * 
   * @param kind Process kind (e.g., 'sidebar', 'todo', 'chat')
   * @param id Process instance ID (must be unique)
   * @param initialState Initial state for the process context
   * @returns Process instance if successful, null if process kind not found
   */
  startProcess(
    kind: string,
    requestedId: string,
    initialState: Record<string, unknown> = {}
  ): Process | null {
    const traceId = trace.start('process-manager-start');

    const canonicalId = SINGLETON_PROCESS_IDS[kind];
    const id = canonicalId ?? requestedId;

    // Check if process already exists
    if (this.processes.has(id)) {
      trace.end(traceId);
      return this.processes.get(id)!.process;
    }

    // Create process via registry
    const process = createProcess(kind, id);
    if (!process) {
      trace.end(traceId);
      return null;
    }

    // Determine lifecycle mode from process metadata
    const lifecycleMode = process.metadata?.preserveState
      ? ProcessLifecycleMode.KEEP_ALIVE
      : ProcessLifecycleMode.STOP_ON_ALL_VIEWS_CLOSE;

    // Create process context
    const context = createProcessContext(
      id,
      bus,
      lifecycleMode,
      initialState
    );

    // Set process reference in context (allows context to notify process)
    context.setProcess(process);

    // CRITICAL: Register process BEFORE starting it
    // This ensures the process is available when views are registered during start()
    // and allocation can look up the process metadata
    const instance: ProcessInstance = {
      process,
      context,
      startTime: Date.now()
    };
    this.processes.set(id, instance);

    // Start process with context (with error handling)
    processErrorBoundary.catchError(id, kind, 'start', () => {
      process.start(context);
    });

    // Check if process failed during start
    if (processErrorBoundary.hasFailed(id)) {
      const error = processErrorBoundary.getError(id);
      log.error(`[ProcessManager] Process ${id} (${kind}) failed to start`, {
        context: error?.context,
        message: error?.error?.message,
        stack: error?.error?.stack
      });

      // Cleanup on process start failure
      const views = context.getViews?.() || [];
      for (const view of views) {
        try {
          view.destroy?.();
        } catch (destroyError) {
          // Ignore cleanup errors
        }
      }

      // Remove process from tracking (was registered before start)
      this.processes.delete(id);

      // Emit failure event (already emitted by error boundary, but emit legacy event too)
      bus.emit('process.failed', {
        processId: id,
        kind,
        error: error?.error.message || 'Unknown error'
      });

      trace.end(traceId);
      return null;
    }

    // Process already registered before start() - just update startTime if needed
    const existingInstance = this.processes.get(id);
    if (existingInstance) {
      existingInstance.startTime = Date.now();
    }

    // Persist process to state (default: persist = true)
    // Views/processes persist by default across page reloads
    // This is independent of preserveState (which is about lifecycle, not persistence)
    // Default to true - all processes persist unless explicitly disabled
    const shouldPersist = true; // Default: all processes persist across reloads
    if (shouldPersist) {
      if (!state.processes) {
        setState('processes', {});
      }
      updateState('processes', (draft) => {
        draft![id] = { kind, id, persist: true, views: [] };
      });
      
      // Capture initial view grid info after a short delay to allow allocation
      // This ensures grid info is captured after views are allocated
      setTimeout(() => {
        this.captureProcessViewGridInfo();
      }, 100);
    }

    // Emit process started event
    bus.emit('process.started', {
      processId: id,
      kind: kind,
      process: process
    });

    trace.end(traceId);

    return process;
  }

  /**
   * Stop a process by ID
   * 
   * @param id Process instance ID
   * @returns true if process was stopped, false if not found
   */
  stopProcess(id: string): boolean {
    const traceId = trace.start('process-manager-stop');

    const instance = this.processes.get(id);
    if (!instance) {
      trace.end(traceId);
      return false;
    }

    const viewCount = instance.context.getViews?.()?.length || 0;

    // CRITICAL: Cleanup process resources before stopping
    // This ensures event listeners, timers, and other resources are properly cleaned up

    // Call process stop hook with error boundary
    processErrorBoundary.catchError(id, instance.process.kind, 'stop', () => {
      if (instance.process.stop) {
        instance.process.stop();
      }
    });

    // Cleanup context resources (event listeners, timers, etc.)
    if (typeof (instance.context as any).cleanup === 'function') {
      (instance.context as any).cleanup();
    }

    // Destroy all views in context
    const views = instance.context.getViews?.() || [];
    for (const view of views) {
      processErrorBoundary.catchError(id, instance.process.kind, 'destroy-view', () => {
        instance.context.destroyView(view.id);
      });
    }

    // Remove process
    this.processes.delete(id);

    // Remove process from persisted state
    if (state.processes && state.processes[id]) {
      updateState('processes', (draft) => {
        if (draft) {
          delete draft[id];
        }
      });
    }

    // Emit process stopped event
    bus.emit('process.stopped', {
      processId: id,
      kind: instance.process.kind
    });

    trace.end(traceId);

    return true;
  }

  /**
   * Get a process by ID
   * 
   * @param id Process instance ID
   * @returns Process instance if found, null otherwise
   */
  getProcess(id: string): Process | null {
    return this.processes.get(id)?.process || null;
  }

  /**
   * Get process context by ID
   * 
   * @param id Process instance ID
   * @returns ProcessContext if found, null otherwise
   */
  getProcessContext(id: string): ProcessContext | null {
    return this.processes.get(id)?.context || null;
  }

  /**
   * Get all running processes
   * 
   * @returns Array of all running processes
   */
  getAllProcesses(): Process[] {
    return Array.from(this.processes.values()).map(instance => instance.process);
  }

  /**
   * Check if a process is running
   * 
   * @param id Process instance ID
   * @returns true if process is running, false otherwise
   */
  isProcessRunning(id: string): boolean {
    return this.processes.has(id);
  }

  /**
   * Stop all processes
   * 
   * Useful for cleanup/shutdown
   */
  stopAllProcesses(): void {
    const processIds = Array.from(this.processes.keys());
    for (const id of processIds) {
      this.stopProcess(id);
    }
  }

  /**
   * Get process instance info (for debugging/monitoring)
   * 
   * @param id Process instance ID
   * @returns Process instance info if found, null otherwise
   */
  getProcessInfo(id: string): { process: Process; context: ProcessContext; startTime: number; viewCount: number } | null {
    const instance = this.processes.get(id);
    if (!instance) return null;

    return {
      process: instance.process,
      context: instance.context,
      startTime: instance.startTime,
      viewCount: instance.context.getViews?.()?.length || 0
    };
  }
}

/**
 * Global Process Manager instance
 * 
 * This is the singleton Process Manager for the entire application.
 */
export const processManager = new ProcessManager();

